import React from 'react';
import { ShieldCheck, Database, RefreshCw, AlertTriangle, ExternalLink } from 'lucide-react';
import { APP_CONFIG } from '../config/appConfig';
import { DisclaimerBanner } from '../components/common/DisclaimerBanner';

export const AboutPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div>
        <h1 className="text-3xl sm:text-4xl font-black text-navy-950">
          About SignalNear & Alert Methodology
        </h1>
        <p className="text-base text-slate-600 mt-2 leading-relaxed">
          Understanding our mission, public data pipeline, and deterministic reporting standards.
        </p>
      </div>

      <DisclaimerBanner />

      {/* Core Mission */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4 shadow-xs">
        <h2 className="text-xl font-bold text-navy-950 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-blue-600" />
          <span>Our Core Promise & Independent Status</span>
        </h2>
        <p className="text-sm text-slate-700 leading-relaxed">
          <strong>SignalNear</strong> is an automated public-information and disruption radar created to answer one simple, crucial question:
        </p>
        <blockquote className="border-l-4 border-blue-600 pl-4 py-2 italic text-sm text-navy-900 bg-slate-50 rounded-r">
          "What official alerts are active in this location, what areas are affected, and what has changed?"
        </blockquote>
        <p className="text-sm text-slate-700 leading-relaxed">
          We operate as an <strong>independent technology platform</strong>. SignalNear is not a government agency, emergency dispatch center, or first-responder organization. We do not create, modify, or censor official government safety instructions.
        </p>
      </section>

      {/* Where Data Comes From */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4 shadow-xs">
        <h2 className="text-xl font-bold text-navy-950 flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-600" />
          <span>Where Does SignalNear Data Come From?</span>
        </h2>
        <p className="text-sm text-slate-700 leading-relaxed">
          100% of the active severe weather alerts presented on SignalNear are retrieved directly from the <strong>United States National Weather Service (NWS)</strong>, a division of the National Oceanic and Atmospheric Administration (NOAA), an agency of the United States federal government.
        </p>
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2 text-xs text-slate-700">
          <div className="font-semibold text-navy-900">Official Endpoint Utilized:</div>
          <code className="text-blue-700 block bg-white p-2 rounded border border-slate-200 overflow-x-auto">
            https://api.weather.gov/alerts/active
          </code>
          <p>
            Data is retrieved in the Common Alerting Protocol (CAP v1.2) standard serialized over GeoJSON.
          </p>
        </div>
      </section>

      {/* How Often Is Data Refreshed? */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4 shadow-xs">
        <h2 className="text-xl font-bold text-navy-950 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 text-blue-600" />
          <span>Data Freshness & Caching Architecture</span>
        </h2>
        <p className="text-sm text-slate-700 leading-relaxed">
          To comply with federal API guidelines and prevent denial-of-service impacts on public infrastructure:
        </p>
        <ul className="list-disc list-inside text-sm text-slate-700 space-y-2 leading-relaxed">
          <li>
            <strong>Polling Interval:</strong> SignalNear caches response payloads with a default Time-To-Live (TTL) of <strong>120 seconds (2 minutes)</strong>.
          </li>
          <li>
            <strong>Deduplication:</strong> Alerts are deduplicated by their permanent NWS record identifiers (<code className="text-xs bg-slate-100 px-1 py-0.5 rounded">properties.id</code>).
          </li>
          <li>
            <strong>Transparent Age Disclosure:</strong> Every page surfaces a live Freshness Indicator showing the exact age in seconds of the cached payload and the timestamp of the last successful synchronization.
          </li>
          <li>
            <strong>Offline / Stale Warning:</strong> If the official API experiences high latency or temporary downtime, SignalNear displays the last recorded snapshot accompanied by an explicit "Cached / Stale Data" warning. An API failure is never reported as "zero alerts exist".
          </li>
        </ul>
      </section>

      {/* Known Limitations */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4 shadow-xs">
        <h2 className="text-xl font-bold text-navy-950 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <span>Known Limitations</span>
        </h2>
        <div className="text-sm text-slate-700 space-y-3 leading-relaxed">
          <p>
            Users should be aware of the following technical boundaries:
          </p>
          <ul className="list-disc list-inside space-y-1.5 pl-2">
            <li>
              <strong>US Coverage Only:</strong> Current verified feeds cover the 50 US States, District of Columbia, Puerto Rico, and US marine zones. International feeds (UK, Canada, Australia) are in preparation and will only launch upon verification.
            </li>
            <li>
              <strong>Geographic Resolution:</strong> Some alerts are issued for entire counties or broadcast zones rather than street-level polygon coordinates. Where polygon coordinates are unavailable, we plot by county or state bounds.
            </li>
            <li>
              <strong>Non-Weather Hazards:</strong> Municipal road closures, local police activity, water main breaks, and neighborhood power line issues are not part of the federal severe weather feed and are not currently represented.
            </li>
          </ul>
        </div>
      </section>

      {/* Official Contacts */}
      <section className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-xs text-slate-600 space-y-2">
        <h3 className="font-bold text-navy-950 text-sm">
          Authoritative Government Channels
        </h3>
        <p>
          Always cross-check emergency orders with authoritative public channels:
        </p>
        <div className="flex flex-wrap gap-4 pt-1 font-medium text-blue-600">
          <a href="https://www.weather.gov" target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">
            weather.gov <ExternalLink className="w-3 h-3" />
          </a>
          <a href="https://www.ready.gov" target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">
            ready.gov <ExternalLink className="w-3 h-3" />
          </a>
          <a href="https://www.fema.gov" target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">
            fema.gov <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </section>
    </div>
  );
};

